import { TicketId, VenueTicket } from "@greeter/core";
import { Log } from "@greeter/log";
import { IVenueApi } from "@greeter/venue-api";
import {
  createFetchMachine,
  FetchActorFields,
  SetterEvents,
  unwrapData,
} from "@greeter/xstate-util";
import { assign, emit, setup } from "xstate";
import { ApiError } from "@greeter/api";
import { indexById } from "@greeter/map";

function ticketSummary(tickets: Array<VenueTicket>) {
  const total = tickets.length;
  const checkedIn = tickets.reduce(
    (acc, curr) => (!!curr.checkedInAt ? acc + 1 : acc),
    0
  );
  return { total: total, checkedIn: checkedIn };
}

export type TicketScannerApiRequirements = Pick<
  IVenueApi,
  | "fetchTicket"
  | "fetchTickets"
  | "saveTicket"
  | "fetchTicketEvent"
  | "saveTickets"
>;

export type TicketScannerEmitted =
  | {
      type: "scanned";
    }
  | { type: "scanning" };

export type TicketScannerEvents =
  | {
      type: "scanSuccess";
      data: TicketId;
    }
  | { type: "scanNext" }
  | { type: "checkAllIn" }
  | SetterEvents<
      Pick<
        TicketScannerContext,
        "ticketSummary" | "ticket" | "status" | "tickets" | "ticketError"
      >
    >;

export type TicketScannerContext = {
  api: FetchActorFields<TicketScannerApiRequirements>;
  latestTicketId?: string;
  status?: "valid" | "already-checked-in";

  ticket?: VenueTicket;
  ticketError?: ApiError;

  tickets?: Map<TicketId, VenueTicket>;
  ticketSummary?: { total: number; checkedIn: number };
};

export const TicketScannerMachine = setup({
  types: {} as {
    context: TicketScannerContext;
    events: TicketScannerEvents;
    emitted: TicketScannerEmitted;
    input: {
      api: TicketScannerApiRequirements;
    };
  },
}).createMachine({
  initial: "scanning",
  context({ input, spawn, self }) {
    const fetchTicketEventRef = spawn(
      createFetchMachine(input.api.fetchTicketEvent.bind(input.api)),
      { syncSnapshot: true }
    );
    const fetchTicketsRef = spawn(
      createFetchMachine(input.api.fetchTickets.bind(input.api)),
      { syncSnapshot: true }
    );
    fetchTicketsRef.on("fetched", (r) => {
      const tickets = r.data;
      self.send({ type: "set.tickets", data: indexById(tickets) });
      self.send({
        type: "set.ticketSummary",
        data: ticketSummary(tickets),
      });
    });
    const fetchTicketRef = spawn(
      createFetchMachine(input.api.fetchTicket.bind(input.api)),
      { syncSnapshot: true }
    );
    const saveTicketRef = spawn(
      createFetchMachine(input.api.saveTicket.bind(input.api)),
      { syncSnapshot: true }
    );
    saveTicketRef.on("error", (e) => Log.error("Failed to save ticket", e));
    saveTicketRef.on("fetched", (r) => {
      self.send({ type: "set.ticket", data: r.data });
    });

    const saveTicketsRef = spawn(
      createFetchMachine(input.api.saveTickets.bind(input.api)),
      { syncSnapshot: true }
    );
    saveTicketsRef.on("fetched", () => {
      const context = self.getSnapshot().context;
      if (context.ticket) {
        fetchTicketsRef.send({
          type: "fetch",
          data: [{ ticketOrderId: context.ticket?.ticketOrderId }],
        });
      }
    });

    fetchTicketRef.on("error", (e) => {
      self.send({ type: "set.ticketError", data: e.error });
    });
    fetchTicketRef.on("fetched", (r) => {
      self.send({ type: "set.ticket", data: r.data });
      self.send({ type: "set.ticketError", data: undefined });
      self.send({
        type: "set.status",
        data: r.data.checkedInAt ? "already-checked-in" : "valid",
      });
      sessionStorage.setItem("ticketEventId", r.data.ticketEventId);
      fetchTicketEventRef.send({
        type: "fetch",
        data: [{ id: r.data.ticketEventId }],
      });
      fetchTicketsRef.send({
        type: "fetch",
        data: [{ ticketOrderId: r.data.ticketOrderId }],
      });
      if (!r.data.checkedInAt) {
        saveTicketRef.send({
          type: "fetch",
          data: [
            {
              id: r.data.id,
              checkedIn: true,
            },
          ],
        });
      }
    });
    return {
      api: {
        fetchTicketRef,
        fetchTicketsRef,
        saveTicketRef,
        saveTicketsRef,
        fetchTicketEventRef,
      },
    };
  },
  on: {
    "set.*": {
      actions: assign(({ event, context }) => {
        switch (event.type) {
          case "set.ticket": {
            const tickets = new Map(context.tickets);

            // TODO: This might be wrong?
            if (!event.data && context.ticket) {
              tickets.delete(context.ticket.id);
            }
            if (event.data) {
              tickets.set(event.data.id, event.data);
            }
            return {
              tickets: tickets,
              ticket: event.data,
              ticketSummary: ticketSummary(Array.from(tickets.values())),
            };
          }
          default: {
            const prop = event.type.split(".").at(-1);
            return { [prop as keyof TicketScannerContext]: event.data };
          }
        }
      }),
    },
    checkAllIn: {
      actions: ({ context }) => {
        const tickets = context.tickets;
        if (tickets) {
          context.api.saveTicketsRef.send({
            type: "fetch",
            data: [
              Array.from(context.tickets?.values() ?? []).map((t) => ({
                id: t.id,
                checkedIn: true,
              })),
            ],
          });
        }
      },
    },
  },
  states: {
    scanning: {
      entry: emit({ type: "scanning" }),
      on: {
        scanSuccess: {
          actions: [
            assign({
              latestTicketId: unwrapData,
            }),
            ({
              context: {
                api: { fetchTicketRef },
              },
              event,
            }) =>
              fetchTicketRef.send({
                type: "fetch",
                data: [{ id: event.data }],
              }),
          ],
          target: "scanned",
        },
      },
    },
    scanned: {
      entry: emit({ type: "scanned" }),
      on: {
        scanNext: {
          target: "scanning",
        },
      },
    },
  },
});
